import React, { useState, useEffect } from 'react';
import {
    Cloud,
    Folder as FolderIcon,
    File as FileIcon,
    FileText,
    Image as ImageIcon,
    Video,
    Music,
    AlertCircle,
    ArrowLeft,
    Download,
    Loader2,
    Home,
    ChevronRight,
    FileArchive
} from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import sharingService from '../services/sharingService';
import { formatFileSize, formatDate } from '../utils/helpers';
import { cn } from '../lib/utils';

// Shadcn UI Components
import { Button } from "../components/ui/button"
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "../components/ui/card"
import { Badge } from "../components/ui/badge"
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert"
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../components/ui/table"

const SharedFolder = () => {
    const { token: rawToken } = useParams();
    const navigate = useNavigate();

    // Clean token
    const cleanToken = rawToken ? rawToken.split('/')[0] : null;

    const [rootFolder, setRootFolder] = useState(null);
    const [currentFolder, setCurrentFolder] = useState(null);
    const [files, setFiles] = useState([]);
    const [subfolders, setSubfolders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [breadcrumbs, setBreadcrumbs] = useState([]);

    useEffect(() => {
        if (!cleanToken) {
            setError('Invalid share link - no token provided');
            setLoading(false);
            return;
        }
        loadSharedFolder();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cleanToken]);

    const loadSharedFolder = async (folderId = null) => {
        try {
            setLoading(true);
            setError('');

            const contents = await sharingService.getSharedFolderContents(cleanToken, folderId);

            if (!rootFolder && contents.rootFolder) {
                setRootFolder(contents.rootFolder);
                setBreadcrumbs([{ id: null, name: contents.rootFolder.name }]);
            }

            setCurrentFolder(contents.folder);
            setFiles(contents.files || []);
            setSubfolders(contents.subfolders || []);

        } catch (err) {
            console.error('Error loading shared folder:', err);
            const errorMessage = err.response?.data?.message ||
                err.message ||
                'Folder tidak ditemukan atau link sudah dicabut';
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleSubfolderClick = (subfolder) => {
        setBreadcrumbs([...breadcrumbs, { id: subfolder.id, name: subfolder.name }]);
        loadSharedFolder(subfolder.id);
    };

    const handleBreadcrumbClick = (index) => {
        const crumb = breadcrumbs[index];
        const newBreadcrumbs = breadcrumbs.slice(0, index + 1);
        setBreadcrumbs(newBreadcrumbs);
        loadSharedFolder(crumb.id);
    };

    const handleFileDownload = (file) => {
        const downloadUrl = `/api/files/download/${file.id}`;
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = file.name;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const getFileIcon = (mimeType) => {
        if (!mimeType) return <FileIcon className="h-5 w-5 text-gray-500" />;
        if (mimeType.includes('pdf')) return <FileText className="h-5 w-5 text-red-500" />;
        if (mimeType.includes('word') || mimeType.includes('document')) return <FileText className="h-5 w-5 text-blue-500" />;
        if (mimeType.startsWith('image')) return <ImageIcon className="h-5 w-5 text-green-500" />;
        if (mimeType.startsWith('video')) return <Video className="h-5 w-5 text-orange-500" />;
        if (mimeType.startsWith('audio')) return <Music className="h-5 w-5 text-purple-500" />;
        if (mimeType.includes('zip') || mimeType.includes('compressed')) return <FileArchive className="h-5 w-5 text-yellow-600" />;
        return <FileIcon className="h-5 w-5 text-gray-500" />;
    };

    const getFileTypeLabel = (mimeType) => {
        if (!mimeType) return 'File';
        if (mimeType.includes('pdf')) return 'PDF';
        if (mimeType.startsWith('image')) return 'Image';
        if (mimeType.startsWith('video')) return 'Video';
        if (mimeType.includes('zip')) return 'Archive';
        return 'File';
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <Loader2 className="h-12 w-12 animate-spin text-primary" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <Card className="max-w-md w-full">
                    <CardHeader>
                        <div className="flex items-center gap-2 text-destructive">
                            <AlertCircle className="h-6 w-6" />
                            <CardTitle>Error</CardTitle>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <p className="text-center text-muted-foreground">{error}</p>
                    </CardContent>
                    <CardFooter className="justify-center">
                        <Button variant="outline" onClick={() => navigate('/')}>
                            Kembali ke Utama
                        </Button>
                    </CardFooter>
                </Card>
            </div>
        );
    }

    const totalItems = files.length + subfolders.length;

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-slate-900 py-10">
            <div className="container max-w-5xl mx-auto px-4">

                {/* Header */}
                <div className="flex items-center gap-3 mb-8">
                    <div className="p-2 bg-primary/10 rounded-lg text-primary">
                        <Cloud className="h-8 w-8" />
                    </div>
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
                            Data Storage
                        </h1>
                        <p className="text-muted-foreground">
                            PDTI-UMI File Sharing
                        </p>
                    </div>
                </div>

                {/* Breadcrumbs */}
                {breadcrumbs.length > 0 && (
                    <nav className="flex mb-4 text-sm font-medium text-muted-foreground overflow-x-auto pb-2">
                        <ol className="flex items-center space-x-2">
                            {breadcrumbs.map((crumb, index) => (
                                <li key={index} className="flex items-center">
                                    {index > 0 && <ChevronRight className="h-4 w-4 mx-1" />}
                                    <button
                                        onClick={() => handleBreadcrumbClick(index)}
                                        className={cn(
                                            "hover:text-primary transition-colors flex items-center",
                                            index === breadcrumbs.length - 1 && "text-foreground font-semibold pointer-events-none"
                                        )}
                                    >
                                        {index === 0 && <Home className="h-4 w-4 mr-1" />}
                                        {crumb.name}
                                    </button>
                                </li>
                            ))}
                        </ol>
                    </nav>
                )}

                <Card className="mb-6">
                    <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                            <div className="p-3 bg-yellow-100 text-yellow-600 rounded-xl dark:bg-yellow-900/30">
                                <FolderIcon className="h-10 w-10" />
                            </div>
                            <div className="flex-1">
                                <h2 className="text-2xl font-bold mb-2">{currentFolder?.name || rootFolder?.name}</h2>
                                <div className="flex gap-2">
                                    <Badge variant="secondary">{totalItems} items</Badge>
                                    <Badge className="bg-yellow-50 text-yellow-700 border-yellow-200 hover:bg-yellow-100">Public Folder</Badge>
                                </div>
                                <Alert className="mt-4 bg-blue-50 text-blue-800 border-blue-200">
                                    <AlertCircle className="h-4 w-4 text-blue-600" />
                                    <AlertDescription>
                                        Folder ini dibagikan secara publik. Login tidak diperlukan.
                                    </AlertDescription>
                                </Alert>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {totalItems === 0 ? (
                    <div className="text-center py-12 bg-white dark:bg-slate-950 rounded-lg border border-dashed">
                        <FolderIcon className="h-16 w-16 text-muted-foreground mx-auto mb-4 opacity-50" />
                        <h3 className="text-lg font-medium">Folder Kosong</h3>
                        <p className="text-muted-foreground">Tidak ada file atau folder di sini.</p>
                    </div>
                ) : (
                    <Card>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Nama</TableHead>
                                    <TableHead className="w-[100px]">Type</TableHead>
                                    <TableHead className="w-[100px]">Ukuran</TableHead>
                                    <TableHead className="w-[150px]">Tanggal</TableHead>
                                    <TableHead className="text-right w-[100px]">Aksi</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {subfolders.map((folder) => (
                                    <TableRow
                                        key={`folder-${folder.id}`}
                                        className="cursor-pointer hover:bg-muted/50"
                                        onClick={() => handleSubfolderClick(folder)}
                                    >
                                        <TableCell>
                                            <div className="flex items-center gap-3">
                                                <div className="p-1.5 bg-yellow-100 text-yellow-600 rounded dark:bg-yellow-900/30">
                                                    <FolderIcon className="h-5 w-5" />
                                                </div>
                                                <span className="font-medium">{folder.name}</span>
                                            </div>
                                        </TableCell>
                                        <TableCell><Badge variant="outline" className="font-normal text-xs">Folder</Badge></TableCell>
                                        <TableCell><span className="text-muted-foreground text-sm">-</span></TableCell>
                                        <TableCell><span className="text-muted-foreground text-sm">{formatDate(folder.createdAt)}</span></TableCell>
                                        <TableCell className="text-right">
                                            <Button variant="ghost" size="sm" className="h-8">Buka</Button>
                                        </TableCell>
                                    </TableRow>
                                ))}

                                {files.map((file) => (
                                    <TableRow key={`file-${file.id}`}>
                                        <TableCell>
                                            <div className="flex items-center gap-3">
                                                <div className="p-1.5 bg-slate-100 text-slate-600 rounded dark:bg-slate-800">
                                                    {getFileIcon(file.mimeType)}
                                                </div>
                                                <span className="font-medium text-sm">{file.name}</span>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant="secondary" className="font-normal text-xs">{getFileTypeLabel(file.mimeType)}</Badge>
                                        </TableCell>
                                        <TableCell>
                                            <span className="text-muted-foreground text-sm font-mono">{formatFileSize(file.size)}</span>
                                        </TableCell>
                                        <TableCell>
                                            <span className="text-muted-foreground text-sm">{formatDate(file.createdAt)}</span>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => handleFileDownload(file)}>
                                                <Download className="h-4 w-4" />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Card>
                )}

                <div className="text-center mt-8">
                    <Button variant="ghost" onClick={() => navigate('/')}>
                        Kembali ke Utama
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default SharedFolder;
